import { z } from 'zod';
import { ConnectorKind } from './kind.js';
/**
 * Per-event sync log row. One Firestore doc per connector event
 * processed (Pub/Sub push, Slack webhook, Stripe webhook,
 * backfill batch). Different from the connector's errorLog (which is
 * capped at 20 entries inline on the OrgConnector doc and only logs
 * failures) — the sync log captures every event regardless of outcome
 * so the admin UI can show a real-time activity feed:
 *
 *   "Gmail: 47 messages ingested in last 24h, 0 failures"
 *   "Slack: 3 channel_created events, 89 message events"
 *
 * The sync log is bounded by a Firestore TTL policy on the `ttlAt`
 * field (operator sets this up in GCP console once; default 30 days).
 * Older docs auto-expire so the collection stays small.
 *
 * Collection: `connectorSyncEvents/{docId}` where docId is unique per
 * event (composite of orgId+kind+eventId+millis to handle retries
 * recording their own attempt).
 */
export const CONNECTOR_SYNC_OUTCOMES = [
    'ingested', // Event processed; graph + audit writes succeeded.
    'skipped', // Event valid but produced no graph write (e.g. duplicate, irrelevant type).
    'duplicate', // Event was already-processed per dedup-store.
    'failed', // Handler threw; recorded via recordConnectorError separately.
];
export const ConnectorSyncOutcome = z.enum(CONNECTOR_SYNC_OUTCOMES);
export const ConnectorSyncEvent = z.object({
    /** Composite doc id — unique across retries. */
    id: z.string().min(1),
    orgId: z.string().min(1),
    kind: ConnectorKind,
    /** Per-user scope only — workspace connectors omit. */
    memberUid: z.string().optional(),
    /** Vendor-supplied event id (Slack event_id, Pub/Sub messageId). */
    eventId: z.string().min(1),
    /** Vendor event type (Slack `event.type`, Gmail `history.update`, etc.). */
    eventType: z.string().min(1),
    outcome: ConnectorSyncOutcome,
    /** Number of graph nodes/edges written (sum). 0 for skipped/failed/duplicate. */
    recordCount: z.number().int().nonnegative(),
    /** Free-form per-event detail (HTTP status, error code, etc.). Bounded ≤ 500 chars JSON. */
    detail: z.record(z.string(), z.unknown()).optional(),
    /** ISO timestamp when the event was processed. */
    at: z.string().min(1),
    /** Firestore-native Timestamp for auto-expiry via TTL policy. */
    ttlAt: z.unknown(), // Date in admin SDK; Firestore Timestamp in client SDK
});
export const CONNECTOR_SYNC_EVENTS_COLLECTION = 'connectorSyncEvents';
/**
 * Response shape for `GET /api/orgs/:o/connectors/:kind/sync-log?limit=50`.
 * The admin UI's "Sync log" panel renders this verbatim.
 */
export const ConnectorSyncLogResponse = z.object({
    orgId: z.string().min(1),
    kind: ConnectorKind,
    events: z.array(ConnectorSyncEvent.omit({ ttlAt: true })),
    /** Rollup counts over the returned window. */
    summary: z.object({
        total: z.number().int().nonnegative(),
        ingested: z.number().int().nonnegative(),
        skipped: z.number().int().nonnegative(),
        duplicate: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        /** Sum of recordCount across all events in the window. */
        totalRecords: z.number().int().nonnegative(),
    }),
});
